'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useOrganization } from '@/contexts/organization-context';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import {
  CreditSaleCatAggregationDto,
  CreditSaleDealerAggregationDto,
  CreditSaleDetailsDto,
  CreditSaleVanAggregationDto,
  PetrohqApiResponseCardSalesAggregationResponse,
  PetrohqApiResponsePagedModelCreditSaleDetailsDto
} from '@/lib/api/data-contracts';
import { useFilterStore } from '@/stores/filter-store';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function CardSales() {
  const { toast } = useToast();
  const { getAllStores } = useOrganization();

  // 온디맨드 방식: API 호출 시점에만 필터 상태 읽기

  // 상태 관리
  const [detailsData, setDetailsData] = useState<CreditSaleDetailsDto[]>([]);
  const [vanAggregation, setVanAggregation] = useState<
    CreditSaleVanAggregationDto[]
  >([]);
  const [terminalAggregation, setTerminalAggregation] = useState<
    CreditSaleCatAggregationDto[]
  >([]);
  const [companyAggregation, setCompanyAggregation] = useState<
    CreditSaleDealerAggregationDto[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    company: false,
    details: false,
    van: false,
    terminal: false,
  });

  // 페이징 관련 상태
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalElements, setTotalElements] = useState(0);
  const [, setIsLast] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // 데이터 가져오기 함수
  const fetchCardSalesData = useCallback(
    async (append = false, pageNumber = currentPage) => {
      // API 호출 시점에 현재 필터 상태 읽기
      const filters = useFilterStore.getState();

      if (!filters.dateRange?.start || !filters.dateRange?.end) {
        return;
      }

      setIsLoading(true);
      try {
        const storeCodeList =
          filters.store.length > 0 ? filters.store : getAllStores();

        // 중앙집중식 파라미터 구축 사용
        const buildCommonParams = () => {
          return filters.buildApiParams('cardsales', storeCodeList);
        };

        // 상세 데이터 API 파라미터
        const detailsParams = buildCommonParams();
        detailsParams.append('page', pageNumber.toString());
        detailsParams.append('size', pageSize.toString());

        // 집계 데이터 API 파라미터
        const aggregationParams = buildCommonParams();

        // API 호출
        let detailsResponse, aggregationResponse;

        if (append) {
          // append 모드에서는 상세 데이터만 가져오기
          detailsResponse = await apiClient.get('sales/cardsales/details', {
            searchParams: detailsParams,
          });
        } else {
          // 초기 로드 시에는 병렬로 모든 데이터 가져오기
          [detailsResponse, aggregationResponse] = await Promise.all([
            apiClient.get('sales/cardsales/details', {
              searchParams: detailsParams,
            }),
            apiClient.get('sales/cardsales/aggregation', {
              searchParams: aggregationParams,
            }),
          ]);
        }

        // 상세 데이터 처리
        const detailsData =
          (await detailsResponse.json()) as PetrohqApiResponsePagedModelCreditSaleDetailsDto;
        if (detailsData.result) {
          const newData = detailsData.result.content || [];

          if (append) {
            setDetailsData(prev => {
              const combined = [...prev, ...newData];
              return combined;
            });
          } else {
            setDetailsData(newData);
          }

          // 페이징 정보 업데이트
          setTotalElements(detailsData.result.page?.totalElements || 0);
          setIsLast(detailsData.result.page?.number === (detailsData.result.page?.totalPages || 0) - 1);
          setHasMore(
            (detailsData.result.page?.number || 0) <
            (detailsData.result.page?.totalPages || 0) - 1
          );
        }

        // 집계 데이터 처리 (append 모드가 아닐 때만)
        if (!append && aggregationResponse) {
          const aggregationData =
            (await aggregationResponse.json()) as PetrohqApiResponseCardSalesAggregationResponse;
          if (aggregationData.result) {
            setVanAggregation(aggregationData.result.vanAggregation || []);
            setTerminalAggregation(aggregationData.result.catAggregation || []);
            setCompanyAggregation(
              aggregationData.result.dealerAggregation || []
            );
          }
        }
      } catch (error) {
        console.error('Card sales data fetch error:', error);
        toast({
          title: '조회 실패',
          description: '신용카드 매출 데이터를 불러오는 중 오류가 발생했습니다',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    },
    [getAllStores, toast]
  );

  // 초기 데이터 로드 및 필터 변경 시 리로드
  useEffect(() => {
    // 필터 변경 시 페이지 리셋
    setCurrentPage(0);
    setDetailsData([]);
    setTotalElements(0);
    setIsLast(false);
    setHasMore(true);
    fetchCardSalesData();
  }, []);

  // 무한 스크롤 - 더 많은 데이터 로드
  const loadMoreData = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchCardSalesData(true, nextPage);
    }
  }, [isLoading, hasMore, currentPage, fetchCardSalesData]);

  // 새로고침 핸들러
  const handleRefresh = () => {
    setCurrentPage(0);
    setDetailsData([]);
    setTotalElements(0);
    setIsLast(false);
    setHasMore(true);
    fetchCardSalesData();
  };

  // 확장/축소 핸들러
  const handleExpandChange = (gridType: string) => (isExpanded: boolean) => {
    setExpanded(prev => ({
      ...prev,
      [gridType]: isExpanded,
    }));
  };

  // 페이지 크기 변경
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setDetailsData([]);
    setTotalElements(0);
    setIsLast(false);
    setHasMore(true);
  };

  return (
    <div>
      {/* 카드사별 집계 */}
      <ExpandableCard
        title='카드사별 집계'
        breadcrumb={
          <Breadcrumb items={menuBreadcrumbItems('cardsales')} />
        }
        pageHeader={
          <PageHeader onSearch={handleRefresh} isLoading={isLoading} />
        }
        onExpandChange={handleExpandChange('company')}
      >
        <div
          className='border rounded-lg'
          style={{ height: expanded.company ? '95%' : '230px' }}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                display: { fitStyle: RealGrid.GridFitStyle.FILL },
                groupPanel: { visible: false },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                footer: { visible: true },
              }}
              rows={companyAggregation}
              layout={[
                {
                  name: '구분',
                  direction: RealGrid.ColumnLayoutDirection.HORIZONTAL,
                  items: ['dealCode', 'dealName'],
                },
                {
                  name: 'POS합계',
                  direction: RealGrid.ColumnLayoutDirection.HORIZONTAL,
                  items: ['cnt1', 'amount1'],
                },
                {
                  name: '단말기합계',
                  direction: RealGrid.ColumnLayoutDirection.HORIZONTAL,
                  items: ['cnt2', 'amount2'],
                },
                {
                  name: 'POS연동단말기',
                  direction: RealGrid.ColumnLayoutDirection.HORIZONTAL,
                  items: ['cnt3', 'amount3'],
                },
                {
                  name: '비연동단말기',
                  direction: RealGrid.ColumnLayoutDirection.HORIZONTAL,
                  items: ['cnt4', 'amount4'],
                },
              ]}
            >
              <RGDataField
                fieldName={'dealCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dealName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cnt1'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amount1'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'cnt2'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amount2'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'cnt3'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amount3'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'cnt4'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amount4'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataColumn fieldName={'dealCode'} header={{ text: '코드' }} />
              <RGDataColumn
                fieldName={'dealName'}
                header={{ text: '카드사명' }}
              />
              <RGDataColumn
                fieldName={'cnt1'}
                header={{ text: '건수' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'amount1'}
                header={{ text: '금액' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'cnt2'}
                header={{ text: '건수' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'amount2'}
                header={{ text: '금액' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'cnt3'}
                header={{ text: '건수' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'amount3'}
                header={{ text: '금액' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'cnt4'}
                header={{ text: '건수' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'amount4'}
                header={{ text: '금액' }}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
              />
            </RealGridReact>
          </div>
        </div>
      </ExpandableCard>

      {/* 카드매출 상세 */}
      <ExpandableCard
        title='카드매출'
        onExpandChange={handleExpandChange('details')}
      >
        <div
          className='border rounded-lg'
          style={{ height: expanded.details ? '95%' : '530px' }}
        >
          <div className='w-full h-full'>
            <RealGridReact
              gridProps={{
                groupPanel: { visible: true },
                checkBar: { visible: false },
                stateBar: { visible: false },
                edit: { readOnly: true, editable: false },
                sorting: {
                  enabled: true,
                  showSortOrder: true,
                  style: RealGrid.SortStyle.INCLUSIVE,
                },
                footer: { visible: true },
              }}
              rows={detailsData}
              onScrollToBottom={loadMoreData}
            >
              <RGDataField
                fieldName={'compCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'compName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'storeName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closedt'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'closecnt'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'sdate'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'stime'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cardMaiibCd'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dealName'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'rcvamount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'creditcardnumber'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'approvalNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'paytype'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'saletype'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'catId'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'tvan'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'fop'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'pumpNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'saleNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'saleSeq'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'beforeCost'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'amount'}
                dataType={RealGrid.ValueType.NUMBER}
              />
              <RGDataField
                fieldName={'netruck'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'dealCode'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'carNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'cardCompanyNo'}
                dataType={RealGrid.ValueType.TEXT}
              />
              <RGDataField
                fieldName={'oilType'}
                dataType={RealGrid.ValueType.TEXT}
              />

              <RGDataColumn fieldName={'storeName'} header={{ text: '매장' }} />
              <RGDataColumn
                fieldName={'closedt'}
                header={{ text: '마감일자' }}
              />
              <RGDataColumn
                fieldName={'closecnt'}
                header={{ text: '차수' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'sdate'}
                header={{ text: '승인일자' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'stime'}
                header={{ text: '승인시간' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'cardMaiibCd'}
                header={{ text: '코드' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'dealName'}
                header={{ text: '매입사명' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'rcvamount'}
                header={{ text: '금액' }}
                width={90}
                numberFormat={'#,##0'}
                styleName={'text-right'}
                footer={{ expression: 'sum', numberFormat: '#,##0' }}
                groupFooter={{ expression: 'sum', numberFormat: '#,##0' }}
              />
              <RGDataColumn
                fieldName={'creditcardnumber'}
                header={{ text: '카드번호' }}
                width={110}
              />
              <RGDataColumn
                fieldName={'approvalNo'}
                header={{ text: '승인번호' }}
                width={90}
              />
              <RGDataColumn
                fieldName={'paytype'}
                header={{ text: '결제구분' }}
                width={80}
                lookupDisplay={true}
                values={['00', '', '01', '02', '03']}
                labels={['승인', '승인', '취소', '유대입금', '유대입금취소']}
              />
              <RGDataColumn
                fieldName={'saletype'}
                header={{ text: '승인구분' }}
                width={120}
                lookupDisplay={true}
                values={[
                  'D1',
                  'd1',
                  'D2',
                  'd2',
                  'MS',
                  'MR',
                  'M3',
                  'M4',
                  'H3',
                  'H4',
                  'JS',
                  'JR',
                  'J3',
                  'J4',
                ]}
                labels={[
                  '신용',
                  '신용+보너스승인',
                  '신용취소',
                  '신용+보너스 취소',
                  '보너스누적',
                  '보너스취소',
                  '보너스사용승인',
                  '보너스사용취소',
                  '현금영수증승인',
                  '현금영수증취소',
                  '자가누적',
                  '자가누적취소',
                  '자사사용',
                  '자가사용취소',
                ]}
              />
              <RGDataColumn fieldName={'catId'} header={{ text: '단말기ID' }} />
              <RGDataColumn
                fieldName={'tvan'}
                header={{ text: 'VAN' }}
                width={80}
              />
              <RGDataColumn
                fieldName={'fop'}
                header={{ text: 'POS' }}
                width={50}
              />
              <RGDataColumn
                fieldName={'pumpNo'}
                header={{ text: '노즐번호' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'saleNo'}
                header={{ text: '판매번호' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'saleSeq'}
                header={{ text: '거래번호' }}
                width={70}
              />
              <RGDataColumn
                fieldName={'cost'}
                header={{ text: '적용단가' }}
                width={70}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'beforeCost'}
                header={{ text: '원단가' }}
                width={90}
              />
              <RGDataColumn
                fieldName={'amount'}
                header={{ text: '적용금액' }}
                width={90}
                numberFormat={'#,##0'}
                styleName={'text-right'}
              />
              <RGDataColumn
                fieldName={'netruck'}
                header={{ text: '네트럭' }}
                width={70}
                lookupDisplay={true}
                values={['1']}
                labels={['Y']}
              />
              <RGDataColumn fieldName={'carNo'} header={{ text: '차량번호' }} />
              <RGDataColumn
                fieldName={'cardCompanyNo'}
                header={{ text: '카드사명' }}
              />
              <RGDataColumn
                fieldName={'oilType'}
                header={{ text: '유종코드' }}
                width={70}
              />
            </RealGridReact>
          </div>
        </div>

        {/* Item Counter */}
        <div className='mt-4 flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <span className='text-sm text-muted-foreground'>
              현재 {detailsData.length}건 / 총 {totalElements.toLocaleString()}
              건
              {currentPage > 0 && (
                <span className='ml-2 text-xs bg-gray-100 px-2 py-1 rounded'>
                  {currentPage + 1}페이지까지 로드됨
                </span>
              )}
            </span>
            {isLoading && (
              <span className='text-sm text-blue-600'>로딩 중...</span>
            )}
            {hasMore && !isLoading && (
              <span className='text-sm text-orange-600'>
                스크롤하여 더 보기
              </span>
            )}
            {!hasMore && totalElements > 0 && (
              <span className='text-sm text-green-600'>
                모든 데이터 로드 완료
              </span>
            )}
          </div>
          <div className='flex items-center space-x-2'>
            <Button
              variant='outline'
              size='sm'
              onClick={handleRefresh}
              disabled={isLoading}
            >
              새로고침
            </Button>
            {hasMore && (
              <Button
                variant='outline'
                size='sm'
                onClick={loadMoreData}
                disabled={isLoading}
              >
                더 보기
              </Button>
            )}
            <div className='border-l pl-2 flex items-center space-x-2'>
              <span className='text-sm text-muted-foreground'>
                페이지 크기:
              </span>
              {[50, 100, 500].map(size => (
                <Button
                  key={size}
                  variant={pageSize === size ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => handlePageSizeChange(size)}
                  disabled={isLoading}
                >
                  {size}
                </Button>
              ))}
              <Button
                variant={pageSize >= 1000 ? 'default' : 'outline'}
                size='sm'
                onClick={() => handlePageSizeChange(totalElements || 1000)}
                disabled={isLoading}
              >
                전체
              </Button>
            </div>
          </div>
        </div>
      </ExpandableCard>

      {/* VAN사별 집계 & 단말기별 집계 */}
      <div className='flex flex-col lg:flex-row gap-4'>
        {/* VAN사별 집계 */}
        <div className='lg:w-1/2'>
          <ExpandableCard
            title='VAN사별 집계'
            onExpandChange={handleExpandChange('van')}
          >
            <div
              className='border rounded-lg'
              style={{ height: expanded.van ? '100%' : '200px' }}
            >
              <div className='w-full h-full'>
                <RealGridReact
                  gridProps={{
                    display: { fitStyle: RealGrid.GridFitStyle.FILL },
                    groupPanel: { visible: false },
                    checkBar: { visible: false },
                    stateBar: { visible: false },
                    edit: { readOnly: true, editable: false },
                    footer: { visible: true },
                  }}
                  rows={vanAggregation}
                >
                  <RGDataField
                    fieldName={'tvan'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'chainId'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'cnt'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'amt'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataColumn
                    fieldName={'tvan'}
                    header={{ text: 'VAN 사' }}
                    mergeRule={'value'}
                  />
                  <RGDataColumn
                    fieldName={'chainId'}
                    header={{ text: '카드사명' }}
                  />
                  <RGDataColumn
                    fieldName={'cnt'}
                    header={{ text: '건수' }}
                    width={40}
                    fillWidth={0}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0' }}
                  />
                  <RGDataColumn
                    fieldName={'amt'}
                    header={{ text: '판매금액' }}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0' }}
                  />
                </RealGridReact>
              </div>
            </div>
          </ExpandableCard>
        </div>

        {/* 단말기별 집계 */}
        <div className='lg:w-1/2'>
          <ExpandableCard
            title='단말기별 집계'
            onExpandChange={handleExpandChange('terminal')}
          >
            <div
              className='border rounded-lg'
              style={{ height: expanded.terminal ? '100%' : '200px' }}
            >
              <div className='w-full h-full'>
                <RealGridReact
                  gridProps={{
                    display: { fitStyle: RealGrid.GridFitStyle.FILL },
                    groupPanel: { visible: false },
                    checkBar: { visible: false },
                    stateBar: { visible: false },
                    edit: { readOnly: true, editable: false },
                    footer: { visible: true },
                  }}
                  rows={terminalAggregation}
                >
                  <RGDataField
                    fieldName={'catId'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'chainId'}
                    dataType={RealGrid.ValueType.TEXT}
                  />
                  <RGDataField
                    fieldName={'cnt'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataField
                    fieldName={'amt'}
                    dataType={RealGrid.ValueType.NUMBER}
                  />
                  <RGDataColumn
                    fieldName={'catId'}
                    header={{ text: '단말기ID' }}
                    mergeRule={'value'}
                  />
                  <RGDataColumn
                    fieldName={'chainId'}
                    header={{ text: '카드사명' }}
                  />
                  <RGDataColumn
                    fieldName={'cnt'}
                    header={{ text: '건수' }}
                    width={40}
                    fillWidth={0}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0' }}
                  />
                  <RGDataColumn
                    fieldName={'amt'}
                    header={{ text: '판매금액' }}
                    numberFormat={'#,##0'}
                    styleName={'text-right'}
                    footer={{ expression: 'sum', numberFormat: '#,##0' }}
                  />
                </RealGridReact>
              </div>
            </div>
          </ExpandableCard>
        </div>
      </div>
    </div>
  );
}
